// src/components/AccessOverridePanel.js
// 
// Access Override panel for UserDetail page
// Add this import to UserDetail.js:
// import AccessOverridePanel from '../components/AccessOverridePanel';
//
// Then add this JSX after the Subscription Paper in UserDetail.js:
// <AccessOverridePanel 
//   userId={userId}
//   userData={userData}
//   onUpdate={(updatedData) => setUserData({ ...userData, ...updatedData })}
// />

import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format, isPast, parseISO } from 'date-fns';

const AccessOverridePanel = ({ userId, userData, onUpdate, sx: sxProp }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [enabled, setEnabled] = useState(false);
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [quantity, setQuantity] = useState('');
  
  // Access status
  const [accessStatus, setAccessStatus] = useState(null);

  useEffect(() => {
    loadAccessStatus();
  }, [userId]);

  const loadAccessStatus = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAccessOverride(userId);
      setAccessStatus(response.data);
      
      // Populate form with current values
      setEnabled(response.data.accessOverride?.enabled || false);
      setReason(response.data.accessOverride?.reason || '');
      setQuantity(response.data.accessOverride?.quantity != null ? String(response.data.accessOverride.quantity) : '');
      if (response.data.accessOverride?.expiresAt) {
        // Format for datetime-local input
        const date = new Date(response.data.accessOverride.expiresAt);
        setExpiresAt(format(date, "yyyy-MM-dd'T'HH:mm"));
      } else {
        setExpiresAt('');
      }
    } catch (err) {
      console.error('Error loading access status:', err);
      // Don't show error - field might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const data = {
        access_override: enabled,
        access_override_reason: enabled ? reason : null,
        access_override_expires_at: enabled && expiresAt ? new Date(expiresAt).toISOString() : null,
        access_override_quantity: enabled && quantity ? parseInt(quantity) : null,
      };

      const response = await adminAPI.updateAccessOverride(userId, data);
      setAccessStatus(response.data);
      setSuccess(enabled ? 'Access override enabled' : 'Access override disabled');
      
      // Notify parent of update
      if (onUpdate) {
        onUpdate({
          access_override: enabled,
          access_override_reason: enabled ? reason : null,
          access_override_expires_at: enabled && expiresAt ? new Date(expiresAt).toISOString() : null,
        });
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update access override');
    } finally {
      setSaving(false);
    }
  };

  // Determine current access source
  const getAccessBadge = () => {
    if (!accessStatus) return null;
    
    const { currentAccess, accessOverride } = accessStatus;
    
    if (currentAccess?.source === 'override') {
      if (accessOverride?.expiresAt && isPast(parseISO(accessOverride.expiresAt))) {
        return (
          <Chip 
            icon={<WarningIcon />}
            label="Override Expired" 
            color="warning" 
            size="small"
          />
        );
      }
      return (
        <Chip 
          icon={<AdminIcon />}
          label="Admin Granted Access" 
          color="info" 
          size="small"
        />
      );
    } else if (currentAccess?.source === 'subscription') {
      return (
        <Chip 
          icon={<CheckIcon />}
          label="Active Subscription" 
          color="success" 
          size="small"
        />
      );
    } else {
      return (
        <Chip 
          icon={<WarningIcon />}
          label="No Access" 
          color="error" 
          size="small"
        />
      );
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, ...sxProp }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, ...sxProp }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminIcon color="primary" />
          Access Override
        </Typography>
        {getAccessBadge()}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            color="primary"
          />
        }
        label="Grant access without subscription"
      />

      <Collapse in={enabled}>
        <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
          <TextField
            fullWidth
            label="Reason"
            placeholder="e.g., beta tester, support resolution, loyalty comp"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="datetime-local"
            label="Expires on (optional)"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty for indefinite access"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Override slots (total people including self)"
            placeholder="e.g., 3"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            size="small"
            inputProps={{ min: 1 }}
            helperText="How many total slots this account gets without billing. Leave empty for access only (no additional people)."
            sx={{ mb: 2 }}
          />

          {!expiresAt && enabled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                No expiration set — user will have access indefinitely until disabled.
              </Typography>
            </Alert>
          )}

          {expiresAt && enabled && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Access will expire: {format(new Date(expiresAt), 'PPpp')}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Show current override info if active */}
      {accessStatus?.accessOverride?.enabled && !enabled && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Currently has admin-granted access
            {accessStatus.accessOverride.reason && (
              <> - Reason: <strong>{accessStatus.accessOverride.reason}</strong></>
            )}
            {accessStatus.accessOverride.expiresAt && (
              <> - Expires: {format(parseISO(accessStatus.accessOverride.expiresAt), 'PPpp')}</>
            )}
          </Typography>
        </Alert>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
        >
          {saving ? 'Saving...' : 'Save Access Settings'}
        </Button>
      </Box>

      {/* Debug info in dev */}
      {process.env.NODE_ENV === 'development' && accessStatus && (
        <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '10px' }}>
            {JSON.stringify(accessStatus, null, 2)}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default AccessOverridePanel;
